import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { 
  FiUsers, FiSettings, FiPlus, FiUser, FiHome, 
  FiMessageSquare, FiBarChart2, FiChevronDown, FiLogOut,
  FiPieChart, FiFileText, FiCalendar, FiDollarSign,
  FiClipboard, FiFolder, FiLayers, FiShield,
  FiClock, FiTrendingUp, FiBook, FiMap,
  FiCoffee, FiBriefcase, FiCreditCard
} from "react-icons/fi";
import { motion, AnimatePresence } from "framer-motion";

// Helper function to decode JWT token
const decodeToken = (token) => {
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    return payload;
  } catch (error) {
    console.error("Error decoding token:", error);
    return null;
  }
};

function Navbar() {
  const [openDropdown, setOpenDropdown] = useState(null);
  const [isHovered, setIsHovered] = useState(null);
  const [userPermissions, setUserPermissions] = useState([]);
  const [userInfo, setUserInfo] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem("authToken");
    if (token) {
      const decoded = decodeToken(token);
      if (decoded) {
        setUserPermissions(decoded.accessPermissions || []);
        setUserInfo({
          name: decoded.teamMemberName,
          role: decoded.role,
          email: decoded.email
        });
      }
    }
  }, []);

  useEffect(() => {
  const loadUserInfo = () => {
    const token = localStorage.getItem("authToken");
    if (token) {
      const decoded = decodeToken(token);
      if (decoded) {
        setUserPermissions(decoded.accessPermissions || []);
        setUserInfo({
          name: decoded.teamMemberName,
          role: decoded.role,
          email: decoded.email
        });
      }
    } else {
      setUserPermissions([]);
      setUserInfo(null);
    }
  };

  loadUserInfo();

  // Listen for changes
  window.addEventListener("authTokenUpdated", loadUserInfo);
  return () => window.removeEventListener("authTokenUpdated", loadUserInfo);
}, []);


  const handleLogout = () => {
    localStorage.removeItem("authToken");
    navigate("/");
  };

  // Define all possible routes with their permissions
  const allRoutes = {
    "franchiseDashboard": "franchiseDashboard",
    "add-employee": "add-employee",
    "employee-list": "employee-list",
    "daily-attendance": "daily-attendance",
    "shift-management": "shift-management",
    "attendance-reports": "attendance-reports",
    "salary-setup": "salary-setup",
    "generate-salary": "generate-salary",
    "policies": "policies",
    "employee-docs": "employee-docs",
    "departments": "departments",
    "designations": "designations",
    "role-permissions": "role-permissions",
    "employee-report": "employee-report",
    "designation": "designation",
    "leave-request": "leave-request",
    "leave-approval": "leave-approval",
    "franchise": "franchise",
    "branch": "branch",
    "leave-type": "leave-type",
    "subscription": "subscription",
    "customer_support": "customer_support",
    "com_tickets": "com_tickets",
    "sup_tickets": "sup_tickets",
    "employee_performance": "employee_performance"
  };


const hasAccess = (route) => {
  // If company, always return true
  if (userInfo?.role === "company") return true;

  return userPermissions.includes(route);
};


  // Group routes into categories based on access permissions
  const getAvailableCategories = () => {
    const categories = [
    
      {
        title: "Employee Management",
        icon: <FiUsers className="text-green-400 text-xl" />,
        cards: [
          { path: "/add-employee", title: "Add Employee", icon: <FiPlus className="text-blue-400" />, requiredPermission: "add-employee" },
           ]
      },
      
      {
        title: "Department Management",
        icon: <FiBriefcase className="text-purple-400 text-xl" />,
        cards: [
         { path: "/departments", title: "Departments", icon: <FiMap className="text-purple-400" />, requiredPermission: "departments" }, // Changed from FiBuilding to FiMap
          { path: "/designations", title: "Designations", icon: <FiLayers className="text-green-400" />, requiredPermission: "designations" },
          { path: "/storedata", title: "Save Data", icon: <FiFolder className="text-orange-400" />, requiredPermission: "storedata" },
         { path: "/shift-management", title: "Shift Management", icon: <FiShield className="text-red-400" />, requiredPermission: "shift-management" },
         { path: "/branch", title: "Add Branch", icon: <FiShield className="text-red-400" />, requiredPermission: "shift-management" },
         
        ]
      },
    
      {
        title: "Support",
        icon: <FiSettings className="text-red-400 text-xl" />,
        cards: [
          { path: "/customer_support", title: "Customer Support", icon: <FiMessageSquare className="text-blue-400" />, requiredPermission: "customer_support" },
          { path: "/com_tickets", title: "Assign Tickets", icon: <FiUser className="text-green-400" />, requiredPermission: "com_tickets" },
          { path: "/employee_performance", title: "Performance", icon: <FiBarChart2 className="text-purple-400" />, requiredPermission: "employee_performance" }
        ]
      }
    ];

    // Filter categories based on user permissions
    return categories
      .map(category => {
        if (category.path) {
          // Single route category (like Dashboard)
          return hasAccess(category.requiredPermission) ? category : null;
        } else {
          // Category with multiple cards
          const accessibleCards = category.cards.filter(card => 
            hasAccess(card.requiredPermission)
          );
          return accessibleCards.length > 0 ? { ...category, cards: accessibleCards } : null;
        }
      })
      .filter(category => category !== null);
  };

  const availableCategories = getAvailableCategories();

  if (availableCategories.length === 0) {
    return (
      <nav className="bg-gradient-to-r from-blue-900 to-purple-900 shadow-2xl sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center justify-between h-16">
            <div className="flex-shrink-0 flex items-center">
              <Link to="/dashboard" className="text-2xl font-bold bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
                Support Tickets
              </Link>
            </div>
            <div className="text-white text-sm">
              No accessible modules
            </div>
            <button 
              onClick={handleLogout}
              className="p-2 text-white hover:bg-white/10 rounded-lg transition-all duration-200"
            >
              <FiLogOut />
            </button>
          </div>
        </div>
      </nav>
    );
  }

  return (
    <nav className="bg-gradient-to-r from-blue-900 to-purple-900 shadow-2xl sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <div className="flex-shrink-0 flex items-center">
            <Link 
              to="/dashboard" 
              className="text-2xl font-bold bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent flex items-center"
            >
              <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-500 rounded-lg flex items-center justify-center mr-2">
                <FiUser className="text-white text-sm" />
              </div>
              Support Tickets
            </Link>
          </div>

          {/* Menu Items */}
          <div className="hidden md:flex space-x-1">
            {availableCategories.map((category, idx) => (
              <div 
                key={idx} 
                className="relative"
                onMouseEnter={() => {
                  setOpenDropdown(idx);
                  setIsHovered(idx);
                }}
                onMouseLeave={() => {
                  setOpenDropdown(null);
                  setIsHovered(null);
                }}
              >
                {category.path ? (
                  // Single link item
                  <Link
                    to={category.path}
                    className="relative flex items-center space-x-2 px-4 py-3 text-white font-semibold transition-all duration-300 group"
                  >
                    <span className="relative z-10">{category.icon}</span>
                    <span className="relative z-10">{category.title}</span>
                    <motion.div
                      className="absolute inset-0 bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg opacity-0 group-hover:opacity-100"
                      initial={false}
                      animate={{ opacity: isHovered === idx ? 0.2 : 0 }}
                      transition={{ duration: 0.2 }}
                    />
                    <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-blue-400 to-purple-400 scale-x-0 group-hover:scale-x-100 transition-transform duration-300" />
                  </Link>
                ) : (
                  // Dropdown item
                  <>
                    <button
                      className="relative flex items-center space-x-2 px-4 py-3 text-white font-semibold transition-all duration-300 group"
                      aria-haspopup="true"
                    >
                      <span className="relative z-10">{category.icon}</span>
                      <span className="relative z-10">{category.title}</span>
                      <FiChevronDown className={`relative z-10 transition-transform duration-200 ${openDropdown === idx ? 'rotate-180' : ''}`} />
                      <motion.div
                        className="absolute inset-0 bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg opacity-0 group-hover:opacity-100"
                        initial={false}
                        animate={{ opacity: isHovered === idx ? 0.2 : 0 }}
                        transition={{ duration: 0.2 }}
                      />
                      <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-blue-400 to-purple-400 scale-x-0 group-hover:scale-x-100 transition-transform duration-300" />
                    </button>

                    {/* Dropdown */}
                    <AnimatePresence>
                      {openDropdown === idx && (
                        <motion.div
                          initial={{ opacity: 0, y: -10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -10 }}
                          transition={{ duration: 0.2 }}
                          className="absolute left-0 mt-1 w-56 bg-white rounded-lg shadow-2xl border border-gray-200 z-50 overflow-hidden"
                        >
                          <div className="py-2">
                            {category.cards.map((card, cardIndex) => (
                              <Link
                                key={cardIndex}
                                to={card.path}
                                className="flex items-center px-4 py-3 text-gray-700 hover:bg-gradient-to-r hover:from-blue-50 hover:to-purple-50 hover:text-blue-600 transition-all duration-200 group/card"
                                onClick={() => setOpenDropdown(null)}
                              >
                                <span className="mr-3 transition-transform duration-200 group-hover/card:scale-110">
                                  {card.icon}
                                </span>
                                <span className="font-medium">{card.title}</span>
                                <div className="ml-auto w-1 h-6 bg-gradient-to-r from-blue-400 to-purple-400 rounded-full scale-y-0 group-hover/card:scale-y-100 transition-transform duration-200" />
                              </Link>
                            ))}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </>
                )}
              </div>
            ))}
          </div>

          {/* User Section */}
          <div className="flex items-center space-x-4">
            {userInfo && (
              <div className="hidden md:flex items-center space-x-3 bg-white/10 backdrop-blur-sm rounded-full px-4 py-2">
                <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center">
                  <FiUser className="text-white text-sm" />
                </div>
                <div className="text-white">
                  <p className="text-sm font-semibold">{userInfo.name}</p>
                  <p className="text-xs opacity-80 capitalize">{userInfo.role}</p>
                </div>
              </div>
            )}

            {/* Logout Button */}
            <button 
              onClick={handleLogout}
              className="p-2 text-white hover:bg-white/10 rounded-lg transition-all duration-200 group/logout"
              title="Logout"
            >
              <FiLogOut className="group-hover/logout:rotate-12 transition-transform duration-200" />
            </button>
          </div>

          {/* Mobile menu button */}
          <button className="md:hidden p-2 text-white">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
        </div>
      </div>

      {/* Mobile Dropdown Overlay */}
      <AnimatePresence>
        {openDropdown !== null && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden"
            onClick={() => setOpenDropdown(null)}
          />
        )}
      </AnimatePresence>
    </nav>
  );
}

export default Navbar;